import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Box,
    Button,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { Check } from '@mui/icons-material';

import { formatToGermanCurrency, formatToGermanNumber } from 'src/common/toGermanNumber';
import StepperActions from './components/StepperActions';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import { changeAwardCriteriaWeights, fetchAwardCriteria } from './offer-evaluation.slice';

export default function OfferEvaluationStepAwardCriteria() {
    const { t } = useTranslation();
    const { tender, awardCriteria } = useAppSelector((state) => state.offerEvaluation);
    const dispatch = useAppDispatch();

    const [priceCriteriaWeight, setPriceCriteriaWeight] = useState(100);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);

        if (!tender) {
            return;
        }

        dispatch(fetchAwardCriteria(tender.id)).then(() => setLoading(false));
    }, [dispatch, tender]);

    useEffect(() => {
        if (awardCriteria) {
            setPriceCriteriaWeight(awardCriteria.priceCriteriaWeight);
        }
    }, [awardCriteria]);

    const handleChangePriceCriteriaWeight = async () => {
        setLoading(true);
        await dispatch(changeAwardCriteriaWeights({ priceCriteriaWeight }));
        setLoading(false);
    };

    return (
        <>
            <Typography variant="h5" gutterBottom>
                {t('Award Criteria')}
            </Typography>

            {loading ? (
                <Skeleton variant="rectangular" width="100%" height={600} />
            ) : (
                <>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            {t('Price (K1)')} –{' '}
                            <TextField
                                type="number"
                                size="small"
                                sx={{ width: 100 }}
                                label={t('Weight')}
                                value={priceCriteriaWeight}
                                onChange={(e) => setPriceCriteriaWeight(Number(e.target.value))}
                                slotProps={{
                                    htmlInput: {
                                        min: 0,
                                        max: 100,
                                    },
                                }}
                            />{' '}
                            %
                            <Button
                                color="secondary"
                                variant="contained"
                                onClick={handleChangePriceCriteriaWeight}
                                sx={{ ml: 1, minWidth: 'unset', width: 40 }}
                            >
                                <Check />
                            </Button>
                        </Typography>

                        <Alert severity="info">
                            {t(
                                'The point value is calculated using linear interpolation, with the upper point limit being defined by the lowest price and the 0-point limit being defined by the double average price.',
                            )}
                        </Alert>

                        <TableContainer component={Paper} sx={{ mt: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            backgroundColor: 'primary.main',
                                            '& .MuiTableCell-root': { color: 'primary.contrastText' },
                                        }}
                                    >
                                        <TableCell>{t('Bidder')}</TableCell>
                                        <TableCell>{t('Price')}</TableCell>
                                        <TableCell>{t('Points')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {awardCriteria?.priceCriteria.map((result, i) => (
                                        <TableRow
                                            key={result.offerSubmissionId}
                                            sx={
                                                i === 0
                                                    ? {
                                                          '& .MuiTableCell-root': {
                                                              backgroundColor: 'secondary.main',
                                                              fontWeight: 'bold',
                                                          },
                                                      }
                                                    : {}
                                            }
                                        >
                                            <TableCell>{result.bidderName}</TableCell>
                                            <TableCell>{formatToGermanCurrency(result.price)}</TableCell>
                                            <TableCell>{formatToGermanNumber(result.points, 3)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            {t('Quality (K2)')} – {100 - priceCriteriaWeight} %
                        </Typography>

                        <Alert severity="info">
                            Bei dieser Ansicht handelt es sich um eine vereinfachte Version der Zuschlagskriterien. Sie
                            ermöglicht die Berechnung der Punktzahl aus dem Preis, verrechnet sie aber noch nicht mit
                            weiteren Kriterien.
                            <br />
                            <br />
                            Daraufbasierend kann die Preispunktzahl in ein Spreadsheet übernommen werden und zur
                            Gesamtpunktzahl verrechnet werden.
                        </Alert>
                    </Box>

                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            {t('Total Point Value')}
                        </Typography>
                        <Alert severity="info">TODO</Alert>
                    </Box>
                </>
            )}

            <StepperActions />
        </>
    );
}
